import { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { Plus, Pencil, Trash2, ShieldCheck, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { confirm } from "@/lib/confirm";

const GROUP_COLORS = {
  Painel: "bg-blue-50 border-blue-200 text-blue-900",
  Cadastros: "bg-emerald-50 border-emerald-200 text-emerald-900",
  Operação: "bg-violet-50 border-violet-200 text-violet-900",
  Financeiro: "bg-amber-50 border-amber-200 text-amber-900",
  Sistema: "bg-rose-50 border-rose-200 text-rose-900",
};

export default function UsersPage() {
  const [items, setItems] = useState([]);
  const [roles, setRoles] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [catalog, setCatalog] = useState(null);
  const [empresas, setEmpresas] = useState([]);
  // permsTouched: true once user manually flipped any permission checkbox.
  // While false, switching role auto-replaces the matrix with the role preset.
  const [permsTouched, setPermsTouched] = useState(false);

  async function load() {
    try { const r = await api.get("/admin/users"); setItems(r.data); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  }
  useEffect(() => {
    load();
    api.get("/auth/roles").then((r) => setRoles(r.data)).catch(() => {});
    api.get("/admin/users/_permissions/catalog").then((r) => setCatalog(r.data)).catch(() => {});
    api.get("/empresas").then((r) => setEmpresas((r.data || []).filter((e) => e.active !== false))).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function openNew() {
    const defaultPerms = catalog?.role_presets?.assistente || [];
    setForm({
      email: "", name: "", password: "",
      role: "assistente",
      active: true,
      permissions: [...defaultPerms],
      empresa_ids: empresas.filter((e) => e.is_default).map((e) => e.id),
    });
    setPermsTouched(false);
    setEditing({});
  }

  function openEdit(u) {
    // Use explicit permissions if set, otherwise the role preset (so the
    // matrix reflects the user's effective permissions today).
    const explicit = Array.isArray(u.permissions) && u.permissions.length > 0
      ? u.permissions
      : (catalog?.role_presets?.[u.role] || u.effective_permissions || []);
    setForm({ ...u, password: "", permissions: [...explicit], empresa_ids: u.empresa_ids || [] });
    setPermsTouched(Array.isArray(u.permissions) && u.permissions.length > 0);
    setEditing(u);
  }

  function changeRole(newRole) {
    if (!permsTouched && catalog) {
      // Auto-apply the new role's preset
      setForm((f) => ({ ...f, role: newRole, permissions: [...(catalog.role_presets[newRole] || [])] }));
    } else {
      setForm((f) => ({ ...f, role: newRole }));
    }
  }

  function togglePerm(key) {
    setPermsTouched(true);
    setForm((f) => {
      const set = new Set(f.permissions || []);
      if (set.has(key)) set.delete(key); else set.add(key);
      return { ...f, permissions: Array.from(set) };
    });
  }

  function toggleEmpresa(id) {
    setForm((f) => {
      const set = new Set(f.empresa_ids || []);
      if (set.has(id)) set.delete(id); else set.add(id);
      return { ...f, empresa_ids: Array.from(set) };
    });
  }

  function resetToRolePreset() {
    if (!catalog) return;
    setForm((f) => ({ ...f, permissions: [...(catalog.role_presets[f.role] || [])] }));
    setPermsTouched(false);
    toast.success("Permissões restauradas para o preset do papel.");
  }

  async function save() {
    setSaving(true);
    try {
      // Decide whether to send permissions: only if user changed anything.
      // For new users we always send them (the matrix may have been touched).
      const sendPerms = permsTouched || !editing.id;
      if (editing.id) {
        const payload = {};
        if (form.name !== editing.name) payload.name = form.name;
        if (form.role !== editing.role) payload.role = form.role;
        if (form.active !== editing.active) payload.active = form.active;
        if (form.password) payload.password = form.password;
        if (sendPerms) payload.permissions = form.permissions || [];
        if (JSON.stringify(form.empresa_ids || []) !== JSON.stringify(editing.empresa_ids || [])) {
          payload.empresa_ids = form.empresa_ids || [];
        }
        await api.patch(`/admin/users/${editing.id}`, payload);
      } else {
        await api.post("/admin/users", {
          email: form.email,
          name: form.name,
          password: form.password,
          role: form.role,
          active: !!form.active,
          permissions: sendPerms ? (form.permissions || []) : null,
          empresa_ids: form.empresa_ids || [],
        });
      }
      toast.success("Salvo");
      setEditing(null);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  }

  async function remove(u) {
    if (!(await confirm({ description: `Excluir usuário ${u.email}?`, variant: "destructive" }))) return;
    try { await api.delete(`/admin/users/${u.id}`); toast.success("Excluído"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  }

  // Group permission definitions for the matrix
  const groupedDefs = useMemo(() => {
    if (!catalog) return [];
    const groups = {};
    catalog.definitions.forEach((d) => {
      groups[d.group] = groups[d.group] || [];
      groups[d.group].push(d);
    });
    return catalog.groups.map((g) => ({ group: g, items: groups[g] || [] }));
  }, [catalog]);

  const isPresetMatch = useMemo(() => {
    if (!catalog || !form.role) return false;
    const preset = new Set(catalog.role_presets[form.role] || []);
    const cur = new Set(form.permissions || []);
    if (preset.size !== cur.size) return false;
    for (const p of preset) if (!cur.has(p)) return false;
    return true;
  }, [catalog, form.role, form.permissions]);

  return (
    <div className="space-y-5" data-testid="users-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display text-3xl font-bold tracking-tight text-slate-900">Usuários</h1>
          <p className="text-sm text-slate-500 mt-1">
            Gestão de acesso e <strong>permissões granulares</strong>. Cada papel já vem com um preset; você pode marcar/desmarcar caixinhas individuais por usuário.
          </p>
        </div>
        <Dialog open={editing !== null} onOpenChange={(o) => { if (!o) setEditing(null); }}>
          <DialogTrigger asChild>
            <Button className="bg-blue-700 hover:bg-blue-800" onClick={openNew} data-testid="users-btn-new">
              <Plus className="h-4 w-4 mr-2" /> Novo usuário
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="user-dialog">
            <DialogHeader>
              <DialogTitle>{editing?.id ? "Editar" : "Novo"} usuário</DialogTitle>
              <DialogDescription>
                Dados de acesso, papel e permissões. As permissões marcadas vêm do papel escolhido — você pode customizar para este usuário específico.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-5 py-2">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {!editing?.id && (
                  <div className="space-y-1.5 md:col-span-2">
                    <Label>E-mail *</Label>
                    <Input type="email" value={form.email || ""}
                      onChange={(e) => setForm({ ...form, email: e.target.value })}
                      data-testid="user-email" />
                  </div>
                )}
                <div className="space-y-1.5">
                  <Label>Nome *</Label>
                  <Input value={form.name || ""}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    data-testid="user-name" />
                </div>
                <div className="space-y-1.5">
                  <Label>Senha {editing?.id ? "(deixe vazio p/ manter)" : "*"}</Label>
                  <Input type="password" value={form.password || ""}
                    onChange={(e) => setForm({ ...form, password: e.target.value })}
                    data-testid="user-password" />
                </div>
                <div className="space-y-1.5">
                  <Label>Papel</Label>
                  <select
                    className="w-full border border-slate-300 rounded-md px-3 h-11 md:h-9 text-base md:text-sm bg-white"
                    value={form.role || ""}
                    onChange={(e) => changeRole(e.target.value)}
                    data-testid="user-role"
                  >
                    {roles.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
                  </select>
                  <p className="text-[11px] text-slate-500">
                    Trocar o papel <strong>preenche o preset</strong> automaticamente — só se você ainda não marcou nada manual.
                  </p>
                </div>
                <div className="flex items-center gap-2 md:col-span-2 pt-1">
                  <Checkbox id="active" checked={!!form.active}
                    onCheckedChange={(v) => setForm({ ...form, active: !!v })}
                    data-testid="user-active" />
                  <Label htmlFor="active" className="cursor-pointer">Ativo</Label>
                </div>
                <div className="md:col-span-2 space-y-1.5 pt-1">
                  <Label>Empresas com acesso</Label>
                  {form.role === "admin" ? (
                    <p className="text-[11px] text-slate-500">
                      Administradores podem acessar <strong>todas as empresas</strong> (selecionando uma por vez no seletor).
                    </p>
                  ) : (
                    <div className="flex flex-wrap gap-4">
                      {empresas.map((e) => (
                        <label key={e.id} className="flex items-center gap-2 text-sm cursor-pointer">
                          <Checkbox
                            checked={(form.empresa_ids || []).includes(e.id)}
                            onCheckedChange={() => toggleEmpresa(e.id)}
                            data-testid={`user-empresa-${e.id}`}
                          />
                          {e.name}
                        </label>
                      ))}
                      {empresas.length === 0 && <span className="text-xs text-slate-400">Carregando empresas…</span>}
                    </div>
                  )}
                </div>
              </div>

              <div className="border-t border-slate-200 pt-4 space-y-3">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div className="flex items-center gap-2">
                    <ShieldCheck className="h-4 w-4 text-blue-700" />
                    <h3 className="text-sm font-semibold text-slate-900">Permissões</h3>
                    <span
                      className={`text-[11px] px-2 py-0.5 rounded-full border ${
                        isPresetMatch
                          ? "bg-slate-50 border-slate-200 text-slate-600"
                          : "bg-amber-50 border-amber-200 text-amber-800"
                      }`}
                      data-testid="perms-state-badge"
                    >
                      {isPresetMatch
                        ? `Preset de ${roles.find((r) => r.value === form.role)?.label || form.role}`
                        : "Customizado p/ este usuário"}
                    </span>
                  </div>
                  {!isPresetMatch && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={resetToRolePreset}
                      data-testid="btn-reset-preset"
                    >
                      <RefreshCw className="h-3.5 w-3.5 mr-1" /> Voltar ao preset
                    </Button>
                  )}
                </div>

                {!catalog && <div className="text-xs text-slate-400">Carregando permissões…</div>}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {groupedDefs.map((g) => (
                    <div
                      key={g.group}
                      className={`rounded-md border p-3 space-y-2 ${GROUP_COLORS[g.group] || "bg-slate-50 border-slate-200"}`}
                    >
                      <div className="text-xs font-bold uppercase tracking-wider">{g.group}</div>
                      <div className="space-y-1.5">
                        {g.items.map((d) => {
                          const checked = (form.permissions || []).includes(d.key);
                          return (
                            <label
                              key={d.key}
                              className="flex items-start gap-2 cursor-pointer text-xs hover:opacity-90"
                            >
                              <Checkbox
                                checked={checked}
                                onCheckedChange={() => togglePerm(d.key)}
                                className="mt-0.5"
                                data-testid={`perm-${d.key}`}
                              />
                              <span className="leading-snug">
                                {d.description}
                                <span className="block text-[10px] opacity-60 font-mono">{d.key}</span>
                              </span>
                            </label>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setEditing(null)}>Cancelar</Button>
              <Button onClick={save} disabled={saving} className="bg-blue-700 hover:bg-blue-800" data-testid="user-save">
                {saving ? "Salvando..." : "Salvar"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <Card className="border-slate-200 shadow-sm">
        <CardContent className="p-0 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200 text-xs uppercase tracking-wider text-slate-600">
              <tr>
                <th className="text-left px-4 py-3">Nome</th>
                <th className="text-left px-4 py-3">E-mail</th>
                <th className="text-left px-4 py-3">Papel</th>
                <th className="text-left px-4 py-3">Permissões</th>
                <th className="text-left px-4 py-3">Ativo</th>
                <th className="w-24"></th>
              </tr>
            </thead>
            <tbody>
              {items.map((u) => {
                const customized = Array.isArray(u.permissions) && u.permissions.length > 0;
                const effCount = (u.effective_permissions || []).length;
                return (
                  <tr key={u.id} className="border-b border-slate-100">
                    <td className="px-4 py-3 font-medium">{u.name}</td>
                    <td className="px-4 py-3">{u.email}</td>
                    <td className="px-4 py-3">{u.role_label || u.role}</td>
                    <td className="px-4 py-3 text-xs">
                      <span className="font-semibold">{effCount}</span>{" "}
                      <span className="text-slate-500">de {catalog?.definitions?.length || "—"}</span>
                      {customized && (
                        <span
                          className="ml-2 inline-block px-1.5 py-0.5 rounded bg-amber-50 border border-amber-200 text-amber-800 text-[10px] uppercase tracking-wider font-semibold"
                          title="Esta pessoa tem permissões customizadas (não usa o preset do papel)"
                        >
                          custom
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3">{u.active ? "Sim" : "Não"}</td>
                    <td className="px-4 py-3 text-right">
                      <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => openEdit(u)} data-testid={`btn-edit-${u.id}`}>
                        <Pencil className="h-3.5 w-3.5" />
                      </Button>
                      <Button size="icon" variant="ghost" className="h-8 w-8 text-red-600" onClick={() => remove(u)} data-testid={`btn-delete-${u.id}`}>
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
}
